import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthStore } from "./stores/auth";
import Login from "./pages/Login";
import Chat from "./pages/Chat";
import NewChat from "./pages/NewChat";
import Sidebar from "./components/Sidebar";
import Profile from "./pages/Profile";
import Memory from "./pages/Memory";
import Header from "./components/Header";
import { UpdateNotification } from "./components/UpdateNotification";
import Health from "./pages/Health";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LANG,
  LANGUAGES,
} from "./components/language-switcher/languages";
import HttpApi from "i18next-http-backend";

i18next
  .use(initReactI18next)
  .use(HttpApi)
  // .use(LanguageDetector)
  .init({
    interpolation: {
      escapeValue: false,
    },
    supportedLngs: LANGUAGES.map((lng) => lng.lng),
    fallbackLng: DEFAULT_LANG,
    debug: true,
    backend: {
      loadPath: `/locales/{{lng}}/{{ns}}.json`,
    },
    react: {
      useSuspense: true,
    },
  });

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function MainLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        // md breakpoint
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on route change in mobile view
  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden">
      <div
        className={`${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out md:translate-x-0 fixed md:static z-30 h-full`}
      >
        <Sidebar />
      </div>
      <div className="flex-1 overflow-hidden relative w-full">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
        />
        <div className="h-[calc(100%-101px)] mt-[40px] py-2 md:py-10 overflow-y-auto">
          <Routes>
            <Route path="/" element={<Navigate to="/new-chat" />} />
            <Route path="/new-chat" element={<NewChat />} />
            <Route path="/chat/:chatId" element={<Chat />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/memory" element={<Memory />} />
          </Routes>
        </div>
      </div>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

function App() {
  const { isAuthenticated, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Router>
      <div className="min-h-screen bg-[#0F1524] text-white">
        <Routes>
          <Route
            path="/login"
            element={!isAuthenticated ? <Login /> : <Navigate to="/new-chat" />}
          />
          <Route path="/health" element={<Health />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          />
        </Routes>
        <UpdateNotification />
      </div>
    </Router>
  );
}

export default App;
